/**
 * トキワ荘（/tokiwa）— TOKIWA_SPEC §5 画面仕様
 * β=2機能: TKW-01 デッサンモード / TKW-02 お題スケッチ。描いた一枚はサーバ保存（決定4）。
 * お題は Admin 手動運用・未設定時は「次のお題を準備中」（決定3）。在室人数など同期表示なし（決定14）。
 */
import { FC, useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { api, TokiwaPrompt as TPrompt, TokiwaSketch } from '../services/api';
import { resolveAssetUrl } from '../hooks/useSiteAssets';
import { SketchCanvas, SketchCanvasHandle } from '../components/town/SketchCanvas';
import { SubmissionModal } from '../components/SubmissionModal';
import { PulseButton } from '../components/ui/PulseButton';
import { useGamenics, GamenicsEvent } from '../hooks/useGamenics';
import { TownShell, TownLoading, TownError, TownEmpty, TownGoals, useTownUser, timeAgo } from '../components/town/townShared';

// ══════════════════════════════════════════════════════════
// 荘マップ /tokiwa
// ══════════════════════════════════════════════════════════
export const Tokiwa: FC = () => {
  const [, setLocation] = useLocation();
  const { user, loginGate } = useTownUser();
  const { playSound } = useGamenics();
  const [sketches, setSketches] = useState<TokiwaSketch[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [pipe, setPipe] = useState<{ blob: Blob; sketchId: string } | null>(null);

  const load = () => {
    setState('loading');
    if (!user) { setSketches([]); setState('ready'); return; }
    api.tokiwaListSketchesMine()
      .then(rows => { setSketches(rows); setState('ready'); })
      .catch(() => setState('error'));
  };
  useEffect(load, [user]);

  const deliver = async (s: TokiwaSketch) => {
    if (!s.image_url) return;
    try {
      const res = await fetch(resolveAssetUrl(s.image_url), { credentials: 'include' });
      const blob = await res.blob();
      setPipe({ blob, sketchId: s.id });
    } catch { /* フェイルセーフ: 取得失敗時は何もしない */ }
  };

  return (
    <TownShell role="illustration" kicker="ILLUSTRATORS' ATELIER" title="トキワ荘">
      <p className="text-ink-2 text-sm mb-6 max-w-2xl">同じ部屋で、同じキャンバスに。上手い下手より「描き切った」を称える、描き手の下宿。</p>

      {/* TKW-03 廊下ギャラリーへの入口（掲げるかは本人の任意＝opt-in／決定20）＋掲示板 */}
      <div className="mb-4 flex flex-wrap gap-2">
        <button
          onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation('/tokiwa/gallery'); }}
          className="btn btn-ghost min-h-[44px]"
        >
          廊下ギャラリーを見る
        </button>
        <button
          onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation('/tokiwa/board'); }}
          className="btn btn-ghost min-h-[44px]"
        >
          掲示板
        </button>
        {/* Phase 3-A: 共有キャンバス（RT-05）。同時に描く部屋へ */}
        <button
          onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation('/tokiwa/rooms'); }}
          className="btn btn-ghost min-h-[44px]"
        >
          合作部屋
        </button>
      </div>

      {/* 2つの部屋 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <button onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation('/tokiwa/dessin'); }}
          className="pop-card text-left p-6 hover:-translate-y-0.5 transition-transform min-h-[44px]">
          <span className="text-[11px] font-bold text-role-illustration-text uppercase tracking-wide">肩慣らし部屋</span>
          <h2 className="font-display font-bold text-ink text-lg mt-1">デッサンモード</h2>
          <p className="text-sm text-ink-2 mt-1">タイマーで区切って描くクロッキー。1人用。</p>
        </button>
        <div className="pop-card p-6 flex flex-col">
          <span className="text-[11px] font-bold text-role-illustration-text uppercase tracking-wide">今週のお題部屋</span>
          <h2 className="font-display font-bold text-ink text-lg mt-1">お題スケッチ</h2>
          <p className="text-sm text-ink-2 mt-1 flex-1">週替わりのモチーフを、自由に一枚。</p>
          <div className="mt-3">
            <PulseButton isPrimary onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation('/tokiwa/prompt'); }}>今週のお題を描く</PulseButton>
          </div>
        </div>
      </div>

      {/* 目標（決定13・実データのみ・0件は非表示） */}
      {user && <TownGoals role="illustration" />}

      {/* 直近スケッチ */}
      <h3 className="text-xs font-bold text-ink-2 mb-3 tracking-wide">直近のスケッチ</h3>
      {state === 'loading' && <TownLoading />}
      {state === 'error' && <TownError message="荘をうまく開けませんでした" onRetry={load} />}
      {state === 'ready' && !user && <TownEmpty title="ログインすると、描いた一枚がここに積み上がります。" />}
      {state === 'ready' && user && sketches.length === 0 && (
        <TownEmpty title="まだ一枚もありません。まずは肩慣らしから。">
          <PulseButton isPrimary onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation('/tokiwa/dessin'); }}>デッサンで肩慣らし</PulseButton>
        </TownEmpty>
      )}
      {state === 'ready' && user && sketches.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {sketches.map(s => (
            <div key={s.id} className="pop-card p-2">
              {s.image_url && <img src={resolveAssetUrl(s.image_url)} alt="スケッチ" className="w-full rounded-pop-sm border border-ink/15 aspect-square object-cover bg-white" />}
              <div className="flex items-center justify-between mt-2">
                <span className="text-[10px] text-ink-3 tabular-nums">{timeAgo(s.created_at)}</span>
                {s.published_post_id ? (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-role-illustration-soft text-role-illustration-text border border-role-illustration">作品化済み</span>
                ) : (
                  <button className="text-[11px] font-bold text-role-illustration-text underline min-h-[32px]" onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); deliver(s); }}>作品として届ける</button>
                )}
              </div>
              {/* TKW-03: 廊下に飾る（opt-in・本人のみ／決定20）。既存の保存→PIPE-01フローには触れない */}
              <GalleryToggle sketch={s} onChanged={load} />
            </div>
          ))}
        </div>
      )}

      {pipe && (
        <SubmissionModal
          postId="genesis"
          role="illustration"
          initialImageBlob={pipe.blob}
          originRef={{ space: 'tokiwa', item_id: pipe.sketchId }}
          onClose={() => setPipe(null)}
          onSuccess={() => { setPipe(null); load(); }}
        />
      )}
      {loginGate}
    </TownShell>
  );
};

/** TKW-03: 保存直後に「廊下に飾る」（opt-in・決定20）。飾らないまま去るのも正しい使い方 */
const GalleryPostSave: FC<{ sketchId: string }> = ({ sketchId }) => {
  const { playSound } = useGamenics();
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  if (done) return <span className="text-[12px] font-bold text-role-illustration-text">廊下に飾りました</span>;
  return (
    <button
      className="btn btn-ghost min-h-[44px]"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        const r = await api.tokiwaToggleGallery(sketchId, true);
        setBusy(false);
        if (r.success) { playSound(GamenicsEvent.TAP_CONFIRM); setDone(true); }
      }}
    >
      {busy ? '飾っています…' : '廊下に飾る'}
    </button>
  );
};

/** TKW-03: 廊下に飾る/外すトグル（opt-in・決定20）。楽観更新せず、成功時のみ再取得（事実性①） */
const GalleryToggle: FC<{ sketch: TokiwaSketch; onChanged: () => void }> = ({ sketch, onChanged }) => {
  const { playSound } = useGamenics();
  const [busy, setBusy] = useState(false);
  const inGallery = (sketch as any).is_in_gallery === 1 || (sketch as any).is_in_gallery === true;

  const toggle = async () => {
    setBusy(true);
    const r = await api.tokiwaToggleGallery(sketch.id, !inGallery);
    setBusy(false);
    if (r.success) { playSound(GamenicsEvent.TAP_CONFIRM); onChanged(); }
  };

  return (
    <button
      onClick={toggle}
      disabled={busy}
      aria-pressed={inGallery}
      className={`mt-1 w-full min-h-[36px] text-[11px] font-bold rounded-pop-sm border-2 px-2 py-1 ${inGallery
        ? 'bg-role-illustration-soft text-role-illustration-text border-role-illustration'
        : 'bg-surface text-ink-2 border-ink/20'}`}
    >
      {busy ? '…' : inGallery ? '廊下に飾っています' : '廊下に飾る'}
    </button>
  );
};

// ══════════════════════════════════════════════════════════
// 廊下ギャラリー /tokiwa/gallery （TKW-03・決定20）
// ══════════════════════════════════════════════════════════
export const TokiwaGallery: FC = () => {
  const [items, setItems] = useState<TokiwaSketch[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  const load = () => {
    setState('loading');
    api.tokiwaGallery()
      .then(rows => { setItems(rows); setState('ready'); })
      .catch(() => setState('error'));
  };
  useEffect(load, []);

  return (
    <TownShell role="illustration" kicker="CORRIDOR GALLERY" title="廊下ギャラリー">
      <p className="text-ink-2 text-sm mb-6 max-w-2xl">
        描き手が「飾る」を選んだ一枚が並びます。順番は日替わりの巡り合わせで、人気順ではありません。
      </p>

      {state === 'loading' && <TownLoading />}
      {state === 'error' && <TownError message="廊下を開けませんでした" onRetry={load} />}
      {state === 'ready' && items.length === 0 && (
        <TownEmpty title="まだ飾られた一枚はありません。あなたの一枚を最初に。" />
      )}
      {state === 'ready' && items.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {items.map(s => (
            <figure key={s.id} className="pop-card p-2 m-0">
              {/* 相対 /file パスは API_BASE で絶対化（本番は別オリジン・§7.4） */}
              {s.image_url && (
                <img src={resolveAssetUrl(s.image_url)} alt="廊下に飾られたスケッチ"
                  className="w-full rounded-pop-sm border border-ink/15 aspect-square object-cover bg-white" />
              )}
              <figcaption className="flex items-center justify-between mt-2 gap-2">
                <GalleryAuthor authorId={s.author_id} />
                {s.published_post_id && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-role-illustration-soft text-role-illustration-text border border-role-illustration flex-shrink-0">作品化済み</span>
                )}
              </figcaption>
            </figure>
          ))}
        </div>
      )}
    </TownShell>
  );
};

/** 作者表示名の解決（解決中はスケルトン・解決不能なら名前を捏造せず「描き手」） */
const GalleryAuthor: FC<{ authorId: string }> = ({ authorId }) => {
  const [name, setName] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    api.getPublicUser(authorId).then(u => { if (!cancelled) setName(u?.display_name ?? null); });
    return () => { cancelled = true; };
  }, [authorId]);

  if (name === undefined) {
    return <span className="inline-block w-16 h-2.5 rounded bg-cream-2 motion-safe:animate-pulse" aria-label="作者を読み込み中" />;
  }
  return <span className="text-[11px] text-ink-2 truncate">{name || '描き手'}</span>;
};

// ══════════════════════════════════════════════════════════
// デッサン部屋 /tokiwa/dessin （TKW-01）
// ══════════════════════════════════════════════════════════
const POSES = [30, 60, 300];
export const TokiwaDessin: FC = () => {
  const { requireLogin, loginGate } = useTownUser();
  const { playSound, triggerCelebration } = useGamenics();
  const canvasRef = useRef<SketchCanvasHandle>(null);
  const [pose, setPose] = useState(60);
  const [remaining, setRemaining] = useState(60);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [pipe, setPipe] = useState<{ blob: Blob; sketchId: string } | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null); // 保存後の「廊下に飾る」対象（TKW-03）

  useEffect(() => { setRemaining(pose); }, [pose]);
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      setRemaining(r => {
        if (r <= 1) { playSound(GamenicsEvent.TAP_CONFIRM); return pose; } // 満了で自動送り（次のポーズへ・βは同一タイマー周回）
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [running, pose]);

  const save = async () => {
    if (canvasRef.current?.isBlank()) { setMsg('まだ何も描かれていないようです'); return; }
    setSaving(true); setMsg(null);
    const blob = await canvasRef.current?.exportPng();
    if (!blob) { setSaving(false); setMsg('画像を書き出せませんでした'); return; }
    const up = await api.uploadImage(new File([blob], 'dessin.png', { type: 'image/png' }));
    if (!up.success || !up.data) { setSaving(false); setMsg(up.error || 'アップロードに失敗しました（手元保存はできます）'); return; }
    const r = await api.tokiwaSaveSketch({ mode: 'dessin', image_url: up.data.url, image_key: up.data.key, elapsed_sec: pose });
    setSaving(false);
    if (r.success) { await triggerCelebration(GamenicsEvent.SUBMISSION_COMPLETE); setMsg('この一枚を残しました。'); if (r.id) { setSavedId(r.id); setPipe({ blob, sketchId: r.id }); } }
    else setMsg(r.error || 'サーバ保存に失敗しました');
  };

  return (
    <TownShell role="illustration" kicker="CROQUIS" title="デッサンモード">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr] gap-6">
        {/* 参照＋タイマー */}
        <div>
          <div className="pop-card p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-ink-2">残り</span>
              {/* reduced-motion時は点滅せず数値＋色コントラストのみ（決定・a11y） */}
              <span className={`font-display font-black text-2xl tabular-nums ${remaining <= 3 && running ? 'text-red-600 motion-safe:animate-pulse' : 'text-ink'}`}>
                {remaining}<span className="text-sm font-normal text-ink-2">秒</span>
              </span>
            </div>
            <div className="flex gap-2 mb-3">
              {POSES.map(p => (
                <button key={p} aria-pressed={pose === p} onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setPose(p); }}
                  className={`btn btn-sm flex-1 min-h-[44px] ${pose === p ? 'btn-primary' : 'btn-ghost'}`}>
                  {p < 60 ? `${p}秒` : `${p / 60}分`}
                </button>
              ))}
            </div>
            <button onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setRunning(v => !v); }} className="btn btn-ghost w-full min-h-[44px]">
              {running ? 'タイマーを止める' : 'タイマーを始める'}
            </button>
          </div>
          <div className="pop-card p-4 text-center text-sm text-ink-3">
            {/* 参照画像は運営登録プリセットのみ（β未整備＝空。キャンバスは白紙で使える／フェイルセーフ） */}
            参照画像プリセットは準備中です。<br />（著作権的に安全な運営登録素材のみを扱います）
          </div>
        </div>

        {/* キャンバス */}
        <div>
          <SketchCanvas ref={canvasRef} />
          {msg && <p className="text-sm text-role-illustration-text mt-3">{msg}</p>}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <PulseButton isPrimary onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); requireLogin(save); }} disabled={saving}>
              {saving ? '残しています…' : 'この一枚を残す'}
            </PulseButton>
            {/* TKW-03: 残した直後に廊下へ飾れる（任意＝opt-in／決定20） */}
            {savedId && <GalleryPostSave sketchId={savedId} />}
          </div>
        </div>
      </div>

      {pipe && (
        <SubmissionModal postId="genesis" role="illustration" initialImageBlob={pipe.blob}
          originRef={{ space: 'tokiwa', item_id: pipe.sketchId }}
          onClose={() => setPipe(null)} onSuccess={() => setPipe(null)} />
      )}
      {loginGate}
    </TownShell>
  );
};

// ══════════════════════════════════════════════════════════
// お題スケッチ部屋 /tokiwa/prompt （TKW-02）
// ══════════════════════════════════════════════════════════
export const TokiwaPromptRoom: FC = () => {
  const { requireLogin, loginGate } = useTownUser();
  const { playSound, triggerCelebration } = useGamenics();
  const canvasRef = useRef<SketchCanvasHandle>(null);
  const [prompt, setPrompt] = useState<TPrompt | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [pipe, setPipe] = useState<{ blob: Blob; sketchId: string } | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null); // 保存後の「廊下に飾る」対象（TKW-03）

  const load = () => {
    setState('loading');
    api.tokiwaCurrentPrompt()
      .then(p => { setPrompt(p); setState('ready'); })
      .catch(() => setState('error'));
  };
  useEffect(load, []);

  const save = async () => {
    if (canvasRef.current?.isBlank()) { setMsg('まだ何も描かれていないようです'); return; }
    setSaving(true); setMsg(null);
    const blob = await canvasRef.current?.exportPng();
    if (!blob) { setSaving(false); setMsg('画像を書き出せませんでした'); return; }
    const up = await api.uploadImage(new File([blob], 'prompt.png', { type: 'image/png' }));
    if (!up.success || !up.data) { setSaving(false); setMsg(up.error || 'アップロードに失敗しました（手元保存はできます）'); return; }
    const r = await api.tokiwaSaveSketch({ mode: 'prompt', image_url: up.data.url, image_key: up.data.key, prompt_id: prompt?.id });
    setSaving(false);
    if (r.success) { await triggerCelebration(GamenicsEvent.SUBMISSION_COMPLETE); setMsg('今週も描き切りましたね。'); if (r.id) { setSavedId(r.id); setPipe({ blob, sketchId: r.id }); } }
    else setMsg(r.error || 'サーバ保存に失敗しました');
  };

  return (
    <TownShell role="illustration" kicker="THEME SKETCH" title="お題スケッチ">
      {state === 'loading' && <TownLoading />}
      {state === 'error' && <TownError message="お題をうまく開けませんでした" onRetry={load} />}
      {state === 'ready' && (
        <>
          {/* お題（Admin手動運用・未設定は「次のお題を準備中」＝決定3。偽のお題を出さない） */}
          <div className="pop-card p-5 mb-5">
            {prompt ? (
              <>
                <span className="text-[11px] font-bold text-role-illustration-text uppercase tracking-wide">今週のモチーフ</span>
                <h2 className="font-display font-bold text-ink text-lg mt-1">{prompt.title}</h2>
                {prompt.description && <p className="text-sm text-ink-2 mt-1">{prompt.description}</p>}
                {prompt.reference_asset_key && (
                  <img src={resolveAssetUrl(`/file/${prompt.reference_asset_key}`)} alt="参考" className="mt-3 max-h-40 rounded-pop-sm border border-ink/15" />
                )}
              </>
            ) : (
              <p className="text-ink-2 text-center py-2">次のお題を準備中です。今は自由に一枚どうぞ。</p>
            )}
          </div>

          <SketchCanvas ref={canvasRef} />
          {msg && <p className="text-sm text-role-illustration-text mt-3">{msg}</p>}
          <p className="text-[11px] text-ink-3 mt-2">上手さの採点はありません。「描き切ったこと」を称えます。</p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <PulseButton isPrimary onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); requireLogin(save); }} disabled={saving}>
              {saving ? '残しています…' : '描き切った！'}
            </PulseButton>
            {/* TKW-03: 描き切った直後に廊下へ飾れる（任意＝opt-in／決定20） */}
            {savedId && <GalleryPostSave sketchId={savedId} />}
          </div>
        </>
      )}

      {pipe && (
        <SubmissionModal postId="genesis" role="illustration" initialImageBlob={pipe.blob}
          originRef={{ space: 'tokiwa', item_id: pipe.sketchId }}
          onClose={() => setPipe(null)} onSuccess={() => setPipe(null)} />
      )}
      {loginGate}
    </TownShell>
  );
};
